/**
 * Editor (builder) de um formulario — /comercial/formularios/:id.
 *
 * Layout em 3 zonas:
 *   (a) Topo  — EntityHeader + card de SETTINGS (nome, tipo, ativo, tema,
 *               mensagens publicas, e — so para CAPTURA/CAMPANHA — etapa
 *               destino + responsavel + origem do lead).
 *   (b) Centro — CANVAS de campos arrastaveis (@dnd-kit) + palette para
 *               adicionar campos por tipo. Cada campo abre o CampoEditorPanel.
 *   (c) Direita — PREVIEW ao vivo (FormRenderer) respeitando a logica
 *               condicional.
 *
 * Persistencia: settings via PATCH /formularios/{id}; campos via
 * POST /formularios/{id}/campos. Ambos via AsyncButton (toast + spinner).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { FiAlertTriangle, FiExternalLink, FiEye, FiPlus, FiSave } from "react-icons/fi";

import AsyncButton from "../../components/AsyncButton";
import ConfirmDialog from "../../components/ConfirmDialog";
import { AvisoDoCampo } from "../../components/EnderecoFields/styles";
import EntityHeader from "../../components/EntityHeader";
import TagsInput from "../../components/TagsInput";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import useAvisoAoSairDaPagina from "../../hooks/useAvisoAoSairDaPagina";
import { ORIGENS_LEAD } from "../../services/crm";
import { buscarCep } from "../../services/enderecos";
import {
  MAX_TAGS_PADRAO,
  MAX_TAMANHO_TAG,
  ORIGEM_LEAD_PADRAO,
  TIPOS_CAMPO,
  TIPOS_FORMULARIO,
  TIPO_CAMPO_LABEL,
  atualizarFormulario,
  obterFormulario,
  obterOpcoesFormulario,
  salvarCampos,
  tipoGeraLead,
  tipoUsaTagsPadrao,
  tipoExigeConvite,
  urlPublicaFormulario,
} from "../../services/formularios";
import { PageContainer, PageState } from "../../styles/shared";

import CampoCard from "./CampoCard";
import CampoEditorPanel from "./CampoEditorPanel";
import { avisoDoBlocoDeEndereco, blocoDeEndereco } from "./blocoEndereco";
import FormRenderer from "./FormRenderer";
import FormTabs from "./FormTabs";
import {
  assinaturaCampos,
  camposCitaveis,
  comChaves,
  destinosUsadosPorOutros,
  novoCampo,
  paraPayload,
  reidratarIds,
  removerCampo,
} from "./camposHelpers";
import { completarEsquemaHttps, erroUrlRedirect } from "./urlRedirect";
import {
  ColorRow,
  EditorAside,
  EditorCard,
  EditorCardBody,
  EditorCardHead,
  EditorFieldset,
  EditorLayout,
  EditorMain,
  CamposLista,
  FieldStack,
  PaletteGrid,
  PaletteItem,
  PreviewFrame,
  PreviewHeader,
  SettingsGrid,
} from "./styles";

const COR_PADRAO = "#5B5BF6";

function rotuloDoCampo(campo) {
  return campo.label || TIPO_CAMPO_LABEL[campo.tipo] || "Campo sem rótulo";
}

function entreAspas(nomes) {
  return nomes.map((nome) => `“${nome}”`).join(", ");
}

// O que o "Salvar configuracoes" grava. Campos de lead so para CAPTURA/CAMPANHA.
function payloadDasConfiguracoes(settings) {
  const payload = {
    nome: settings.nome.trim(),
    descricao: settings.descricao,
    tipo: settings.tipo,
    ativo: settings.ativo,
    config: {
      ...settings.config,
      redirect_url: completarEsquemaHttps(settings.config.redirect_url),
    },
  };
  if (tipoGeraLead(settings.tipo)) {
    payload.etapa_destino = settings.etapa_destino || null;
    payload.responsavel_padrao = settings.responsavel_padrao || null;
    // Origem vazia virava "Outro" em todo lead do site e zerava o Site no
    // relatorio de origem; hoje o servidor recusa com 400 (FORM-29).
    payload.origem_lead = settings.origem_lead || ORIGEM_LEAD_PADRAO;
  }
  // Tags do lead valem tambem para ENRIQUECER (o service as mescla) — por
  // isso fora do `geraLead` (FORM-30).
  if (tipoUsaTagsPadrao(settings.tipo)) {
    payload.tags_padrao = settings.tags_padrao;
  }
  return payload;
}

// Texto que muda quando muda algo que o "Salvar configuracoes" grava (FORM-31).
function assinaturaConfiguracoes(settings) {
  return JSON.stringify(payloadDasConfiguracoes(settings));
}

// Settings default (defensivo enquanto o backend ainda nao respondeu).
function settingsIniciais() {
  return {
    nome: "",
    descricao: "",
    tipo: "CAPTURA_LEAD",
    ativo: false,
    slug: "",
    etapa_destino: "",
    // So leitura: nome e situacao do que esta gravado, para o select mostrar
    // a coluna que saiu do pipeline em vez de cair em "Etapa de entrada".
    etapa_destino_nome: "",
    etapa_destino_ativa: null,
    responsavel_padrao: "",
    responsavel_padrao_nome: "",
    origem_lead: ORIGEM_LEAD_PADRAO,
    tags_padrao: [],
    config: {
      tema_cor: COR_PADRAO,
      mensagem_sucesso: "",
      redirect_url: "",
      titulo_publico: "",
      subtitulo_publico: "",
    },
  };
}

export default function Editor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { request, hasAllPermissions } = useAuth();
  const toast = useToast();
  // Quem so tem formulario:view ve o Editor em modo leitura: o backend exige
  // formulario:manage para gravar, e a pessoa so descobria no Salvar (FORM-41).
  const podeEditar = hasAllPermissions(["formulario:manage"]);
  // O preview preenche o endereco pelo CEP como a pagina publica, para o
  // gestor ver o mapeamento funcionando antes de publicar.
  const buscarCepDoPreview = useCallback((cep) => buscarCep(request, cep), [request]);

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [settings, setSettings] = useState(settingsIniciais);
  const [campos, setCampos] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  // Sem as opcoes nao da para escolher etapa nem responsavel: a tela diz por
  // que e desabilita os selects, em vez de oferecer uma lista inventada.
  const [erroOpcoes, setErroOpcoes] = useState("");
  // Alerta persistente de captura sem contato mapeado (vem do backend).
  const [avisoMapeamento, setAvisoMapeamento] = useState("");
  const [erroTags, setErroTags] = useState("");
  // Respostas ja recebidas: excluir campo gravado pede confirmacao (FORM-17).
  const [totalRespostas, setTotalRespostas] = useState(0);
  const [excluindo, setExcluindo] = useState(null); // campo | null
  const [erroRedirect, setErroRedirect] = useState("");
  // Assinatura do que esta gravado, por bloco: base do aviso de alteracao nao
  // salva. Os dois "Salvar" sao independentes e trocar de aba desmonta a tela.
  const [salvoConfiguracoes, setSalvoConfiguracoes] = useState("");
  const [salvoCampos, setSalvoCampos] = useState("");
  // Navegacao esperando confirmacao: { tipo: "sair" | "abrir", acao } | null.
  const [confirmacao, setConfirmacao] = useState(null);

  // Painel de edicao de campo. `novo` = acabou de sair da palette e ainda
  // nao foi confirmado. Os campos que a condicao pode citar sao calculados
  // na hora, a partir da lista atual (qualquer campo sem ciclo — FORM-09).
  const [editando, setEditando] = useState(null); // { campo, novo } | null

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // ---------- Carregar ----------
  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const data = await obterFormulario(request, id);
      const carregadas = {
        nome: data.nome || "",
        descricao: data.descricao || "",
        tipo: data.tipo || "CAPTURA_LEAD",
        ativo: Boolean(data.ativo),
        slug: data.slug || "",
        etapa_destino: data.etapa_destino ?? "",
        etapa_destino_nome: data.etapa_destino_nome || "",
        etapa_destino_ativa: data.etapa_destino_ativa ?? null,
        responsavel_padrao: data.responsavel_padrao ?? "",
        responsavel_padrao_nome: data.responsavel_padrao_nome || "",
        origem_lead: data.origem_lead || ORIGEM_LEAD_PADRAO,
        tags_padrao: Array.isArray(data.tags_padrao) ? data.tags_padrao : [],
        config: {
          // Espalha o config INTEIRO (o `design` da aba Design etc.): o
          // PATCH manda o bloco completo e o backend substitui, nao mescla.
          // Tambem e' o que faz o preview aqui embaixo vestir o design.
          ...(data.config || {}),
          tema_cor: data.config?.tema_cor || COR_PADRAO,
          mensagem_sucesso: data.config?.mensagem_sucesso || "",
          redirect_url: data.config?.redirect_url || "",
          titulo_publico: data.config?.titulo_publico || "",
          subtitulo_publico: data.config?.subtitulo_publico || "",
        },
      };
      const lista = comChaves(data.campos || []);
      setSettings(carregadas);
      setCampos(lista);
      setSalvoConfiguracoes(assinaturaConfiguracoes(carregadas));
      setSalvoCampos(assinaturaCampos(lista));
      setAvisoMapeamento(data.aviso_mapeamento || "");
      setTotalRespostas(Number(data.total_respostas) || 0);
    } catch (err) {
      setErro(err?.message || "Erro ao carregar o formulario.");
    } finally {
      setLoading(false);
    }
  }, [request, id]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Colunas do CRM e vendedores numa action so, que exige a MESMA permissao
  // desta tela (formulario:manage). Antes vinham de /api/crm/pipeline
  // (lead:view) e /api/usuarios (usuario:view): o cargo Marketing levava 403
  // silencioso, o select caia no PIPELINE_PADRAO com ids "seed-contato" e o
  // Salvar respondia "Deve ser um UUID válido" (FORM-28).
  useEffect(() => {
    let vivo = true;
    obterOpcoesFormulario(request, { silentError: true })
      .then((data) => {
        if (!vivo) return;
        setEtapas(Array.isArray(data?.etapas) ? data.etapas : []);
        setUsuarios(Array.isArray(data?.usuarios) ? data.usuarios : []);
        setErroOpcoes("");
      })
      .catch((err) => {
        if (vivo) setErroOpcoes(err?.message || "Falha ao carregar as opções.");
      });
    return () => {
      vivo = false;
    };
  }, [request]);

  const geraLead = tipoGeraLead(settings.tipo);
  const usaTags = tipoUsaTagsPadrao(settings.tipo);
  const opcoesIndisponiveis = Boolean(erroOpcoes);
  // Coluna e vendedor GRAVADOS que nao estao nas opcoes continuam no select:
  // some-los faria a tela cair na primeira opcao e esconder o que esta valendo.
  const etapaNasOpcoes = etapas.some(
    (e) => String(e.id) === String(settings.etapa_destino)
  );
  const mostrarEtapaSalva = Boolean(settings.etapa_destino) && !etapaNasOpcoes;
  const etapaForaDoPipeline = settings.etapa_destino_ativa === false;
  const rotuloEtapaSalva = `${settings.etapa_destino_nome || "Coluna atual"}${
    etapaForaDoPipeline ? " (inativa)" : ""
  }`;
  const responsavelNasOpcoes = usuarios.some(
    (u) => String(u.id) === String(settings.responsavel_padrao)
  );
  const mostrarResponsavelSalvo =
    Boolean(settings.responsavel_padrao) && !responsavelNasOpcoes;
  // Fora da lista com as opcoes carregadas = vendedor inativo (a action so
  // devolve ativos). Sem as opcoes, so o nome — nao da para afirmar nada.
  const rotuloResponsavelSalvo = `${
    settings.responsavel_padrao_nome || "Vendedor atual"
  }${opcoesIndisponiveis ? "" : " (inativo)"}`;

  // ---------- Settings ----------
  const setCampoSettings = (chave, valor) =>
    setSettings((prev) => ({ ...prev, [chave]: valor }));

  const setConfig = (chave, valor) =>
    setSettings((prev) => ({
      ...prev,
      config: { ...prev.config, [chave]: valor },
    }));

  // O servidor recusa a lista inteira acima do teto; dizer aqui evita perder
  // o resto das configuracoes por causa de uma tag a mais (FORM-30).
  const aplicarTags = (lista) => {
    if (lista.length > MAX_TAGS_PADRAO) {
      setErroTags(`Use no máximo ${MAX_TAGS_PADRAO} tags padrão.`);
      return;
    }
    setErroTags("");
    setCampoSettings("tags_padrao", lista);
  };

  // URL sem esquema vira https no blur; outro esquema (javascript:, data:)
  // fica como esta e ganha o erro inline (FORM-02).
  const completarRedirect = (valor) => {
    const completo = completarEsquemaHttps(valor);
    if (completo !== valor) setConfig("redirect_url", completo);
    setErroRedirect(erroUrlRedirect(completo));
  };

  const salvarSettings = useCallback(async () => {
    const payload = payloadDasConfiguracoes(settings);
    const erroUrl = erroUrlRedirect(payload.config.redirect_url);
    setErroRedirect(erroUrl);
    if (erroUrl) {
      document.getElementById("form-redirect")?.focus();
      throw new Error(erroUrl);
    }
    const atualizado = await atualizarFormulario(request, id, payload);
    setSalvoConfiguracoes(JSON.stringify(payload));
    // Sincroniza slug (pode ter sido (re)gerado a partir do nome no backend).
    if (atualizado?.slug) setCampoSettings("slug", atualizado.slug);
    setAvisoMapeamento(atualizado?.aviso_mapeamento || "");
  }, [request, id, settings]);

  // ---------- Campos: dnd + CRUD ----------
  // A ordem e' livre: a condicao pode citar campo que vem depois (o backend
  // aceita referencia para frente e recusa so o ciclo — FORM-09).
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = campos.findIndex((c) => String(c._key) === active.id);
    const newIndex = campos.findIndex((c) => String(c._key) === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    setCampos(arrayMove(campos, oldIndex, newIndex));
  };

  const adicionarCampo = (tipo) => {
    // E-mail e telefone ja nascem mapeados quando o destino esta livre: o
    // lead chegava ao CRM sem ninguem para chamar (FORM-22).
    const campo = novoCampo(tipo, campos.length, destinosUsadosPorOutros(campos));
    setCampos((prev) => [...prev, campo]);
    // Abre direto a edicao do campo recem-criado.
    setEditando({ campo, novo: true });
  };

  // Endereco no lead sao SETE destinos (ADR-0071). A paleta insere o bloco
  // inteiro, com o CEP na frente: sem campo de CEP o preenchimento automatico
  // nao liga (`podeBuscarCep`). Destino que outro campo ja usa fica de fora —
  // o servidor aceita um campo por destino.
  const adicionarBlocoEndereco = () => {
    const resultado = blocoDeEndereco(
      campos.length,
      destinosUsadosPorOutros(campos)
    );
    if (!resultado.campos.length) {
      toast.warning(avisoDoBlocoDeEndereco(resultado));
      return;
    }
    setCampos((prev) => [...prev, ...resultado.campos]);
    toast.success(avisoDoBlocoDeEndereco(resultado));
  };

  const abrirEdicao = (campo) => {
    setEditando({ campo, novo: false });
  };

  // Cancelar (botao, Esc, clique fora) o campo que acabou de sair da palette
  // tira o card: ficava "Campo sem rotulo" e o proximo "Salvar campos"
  // recusava o lote inteiro (FORM-38).
  const fecharPainel = () => {
    if (editando?.novo) {
      const chave = editando.campo._key;
      setCampos((prev) => prev.filter((c) => c._key !== chave));
    }
    setEditando(null);
  };

  const removerDoCanvas = (campo) => {
    const { campos: restantes, afetados } = removerCampo(campos, campo._key);
    setCampos(restantes);
    if (afetados.length > 0) {
      toast.warning(
        `As regras que dependiam de “${rotuloDoCampo(campo)}” saíram de ${entreAspas(afetados)}. Campo que ficou sem regra passa a aparecer sempre.`
      );
    }
  };

  // Campo ja gravado num formulario com respostas: confirma antes. O valor
  // continua em Respostas pelo snapshot (FORM-17), mas o gestor precisa
  // saber que esta tirando um campo que gente ja respondeu.
  const excluirCampo = (campo) => {
    if (campo.id && totalRespostas > 0) {
      setExcluindo(campo);
      return;
    }
    removerDoCanvas(campo);
  };

  const salvarCampoEditado = (campoAtualizado) => {
    setCampos((prev) =>
      prev.map((c) =>
        c._key === campoAtualizado._key ? { ...c, ...campoAtualizado } : c
      )
    );
    setEditando(null);
  };

  const salvarTodosCampos = useCallback(async () => {
    const enviados = campos;
    const atualizado = await salvarCampos(request, id, paraPayload(enviados));
    // O campo criado nesta sessao precisa do UUID devolvido: sem ele, o
    // proximo salvamento o recriava e as respostas recebidas no meio ficavam
    // orfas (FORM-04). A chamada fora do setState falha antes de mexer no
    // canvas se a resposta nao tiver os campos.
    reidratarIds(enviados, enviados, atualizado?.campos);
    setCampos((atuais) => reidratarIds(atuais, enviados, atualizado.campos));
    setSalvoCampos(assinaturaCampos(enviados));
    // Mapear o nome e o telefone resolve o alerta de captura sem contato:
    // o servidor recalcula o aviso a cada gravacao de campos (FORM-22).
    setAvisoMapeamento(atualizado?.aviso_mapeamento || "");
  }, [request, id, campos]);

  const copiarLink = useCallback(async () => {
    const url = urlPublicaFormulario(settings.slug);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link público copiado.");
    } catch (_) {
      toast.info(url);
    }
  }, [settings.slug, toast]);

  // ---------- Preview: re-monta quando campos/config mudam ----------
  const previewKey = useMemo(
    () =>
      JSON.stringify(
        campos.map((c) => [
          c._key,
          c.tipo,
          c.label,
          c.obrigatorio,
          c.largura,
          c.opcoes,
          c.condicao,
        ])
      ),
    [campos]
  );

  const tituloPublico = settings.config.titulo_publico || settings.nome;

  // ---------- Alteracoes nao salvas (FORM-31) ----------
  // Antes da carga nao ha assinatura gravada, e nada a perder.
  const blocosAlterados =
    loading || erro
      ? []
      : [
          assinaturaConfiguracoes(settings) !== salvoConfiguracoes && "Configurações",
          assinaturaCampos(campos) !== salvoCampos && "Campos",
        ].filter(Boolean);
  const haAlteracoes = blocosAlterados.length > 0;
  const blocosPorExtenso = blocosAlterados.join(" e ");
  useAvisoAoSairDaPagina(haAlteracoes);

  // Abas, voltar e "Abrir pagina publica" confirmam quando ha alteracao: a
  // rota desmonta o Editor e o estado local se perde; a pagina publica mostra
  // so o que ja foi salvo.
  const seguirOuConfirmar = (tipo, acao) => {
    if (!haAlteracoes) {
      acao();
      return;
    }
    setConfirmacao({ tipo, acao });
  };
  const cancelarConfirmacao = useCallback(() => setConfirmacao(null), []);
  const confirmandoAbrir = confirmacao?.tipo === "abrir";

  if (loading) {
    return (
      <PageContainer>
        <PageState>Carregando formulario...</PageState>
      </PageContainer>
    );
  }

  if (erro) {
    return (
      <PageContainer>
        <EntityHeader
          onBack={() => navigate("/comercial/formularios")}
          backLabel="Voltar"
          title="Formulario"
        />
        <PageState $tone="error">{erro}</PageState>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <EntityHeader
        onBack={() => seguirOuConfirmar("sair", () => navigate("/comercial/formularios"))}
        backLabel="Formularios"
        title={settings.nome || "Formulario sem nome"}
        subtitle={
          settings.slug ? `/f/${settings.slug}` : "Salve para gerar o link público"
        }
        badges={[
          {
            key: "tipo",
            label:
              TIPOS_FORMULARIO.find((t) => t.value === settings.tipo)?.label ||
              settings.tipo,
            tone: "neutral",
          },
          {
            key: "ativo",
            label: settings.ativo ? "Ativo" : "Inativo",
            tone: settings.ativo ? "success" : "neutral",
          },
          haAlteracoes
            ? { key: "pendente", label: "Alterações não salvas", tone: "warning" }
            : null,
        ].filter(Boolean)}
        actions={[
          // FORM-12: ENRIQUECER_LEAD nao tem link divulgavel — o convite
          // pessoal sai do cockpit do lead (Enviar formulario).
          settings.slug && settings.ativo && !tipoExigeConvite(settings.tipo)
            ? {
                key: "abrir",
                label: "Abrir página pública",
                icon: <FiExternalLink />,
                onClick: () =>
                  seguirOuConfirmar("abrir", () =>
                    window.open(urlPublicaFormulario(settings.slug), "_blank", "noopener")
                  ),
              }
            : null,
          settings.slug && !tipoExigeConvite(settings.tipo)
            ? { key: "copiar", label: "Copiar link público", onClick: copiarLink }
            : null,
        ].filter(Boolean)}
      />

      <FormTabs
        id={id}
        ativa="editor"
        onNavegar={(destino) => seguirOuConfirmar("sair", () => navigate(destino))}
      />

      {!podeEditar && (
        <div className="so-banner info" role="note" style={{ marginBottom: 12 }}>
          <span>
            Você pode ver este formulário, mas não alterá-lo: falta a permissão
            para gerenciar formulários.
          </span>
        </div>
      )}

      {/* ---- (a) Settings ---- */}
      <EditorCard>
        <EditorCardHead>
          <h2>Configurações</h2>
          {/* silentError: URL invalida aparece no proprio campo, e erro da
              API ja sai no toast automatico do request. */}
          {podeEditar && (
            <AsyncButton
              variant="primary"
              onClick={salvarSettings}
              successMessage="Configurações salvas."
              silentError
            >
              <FiSave size={14} aria-hidden="true" /> Salvar configuracoes
            </AsyncButton>
          )}
        </EditorCardHead>
        <EditorCardBody>
          {/* Captura publicada sem nome e sem telefone/e-mail mapeados: o card
              chega ao CRM sem ninguem para chamar. O servidor barra a ATIVACAO;
              o que ja esta ativo continua no ar, e o aviso fica (FORM-22). */}
          {avisoMapeamento && (
            <div className="so-banner warn" role="status" style={{ marginBottom: 12 }}>
              <FiAlertTriangle aria-hidden="true" />
              <span>{avisoMapeamento}</span>
            </div>
          )}
          {erroOpcoes && (
            <div className="so-banner warn" role="status" style={{ marginBottom: 12 }}>
              <FiAlertTriangle aria-hidden="true" />
              <span>
                Não foi possível carregar as colunas do CRM e os vendedores
                ({erroOpcoes}). A etapa de destino e o responsável padrão ficam
                como estão até você recarregar a página.
              </span>
            </div>
          )}
          <EditorFieldset disabled={!podeEditar}>
            <SettingsGrid>
              <FieldStack className="full">
                <label className="input-label" htmlFor="form-nome">
                  Nome (interno)
                </label>
                <input
                  id="form-nome"
                  className="input"
                  type="text"
                  value={settings.nome}
                  maxLength={200}
                  onChange={(e) => setCampoSettings("nome", e.target.value)}
                  placeholder="Ex: Captacao site — cozinhas"
                />
              </FieldStack>

              <FieldStack>
                <label className="input-label" htmlFor="form-tipo">
                  Tipo
                </label>
                <select
                  id="form-tipo"
                  className="select"
                  value={settings.tipo}
                  onChange={(e) => setCampoSettings("tipo", e.target.value)}
                >
                  {TIPOS_FORMULARIO.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </FieldStack>

              <FieldStack>
                <span className="input-label">Status</span>
                <label
                  className="input-help"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                    color: "var(--ink)",
                    fontSize: 13,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={settings.ativo}
                    onChange={(e) => setCampoSettings("ativo", e.target.checked)}
                    style={{ width: 16, height: 16 }}
                  />
                  Formulario ativo (aceita respostas no link publico)
                </label>
              </FieldStack>

              <FieldStack>
                <span className="input-label">Cor do tema</span>
                <ColorRow>
                  <input
                    type="color"
                    value={settings.config.tema_cor || COR_PADRAO}
                    onChange={(e) => setConfig("tema_cor", e.target.value)}
                    aria-label="Cor do tema do formulario"
                  />
                  <span className="hex">{settings.config.tema_cor || COR_PADRAO}</span>
                </ColorRow>
              </FieldStack>

              <FieldStack className="full">
                <label className="input-label" htmlFor="form-titulo-pub">
                  Titulo publico
                </label>
                <input
                  id="form-titulo-pub"
                  className="input"
                  type="text"
                  value={settings.config.titulo_publico}
                  maxLength={200}
                  onChange={(e) => setConfig("titulo_publico", e.target.value)}
                  placeholder="Cabeçalho visto pelo cliente (vazio = usa o nome)"
                />
              </FieldStack>

              <FieldStack className="full">
                <label className="input-label" htmlFor="form-subtitulo-pub">
                  Subtitulo publico
                </label>
                <input
                  id="form-subtitulo-pub"
                  className="input"
                  type="text"
                  value={settings.config.subtitulo_publico}
                  maxLength={300}
                  onChange={(e) => setConfig("subtitulo_publico", e.target.value)}
                  placeholder="Texto curto abaixo do título"
                />
              </FieldStack>

              <FieldStack className="full">
                <label className="input-label" htmlFor="form-msg-sucesso">
                  Mensagem de sucesso
                </label>
                <input
                  id="form-msg-sucesso"
                  className="input"
                  type="text"
                  value={settings.config.mensagem_sucesso}
                  maxLength={300}
                  onChange={(e) => setConfig("mensagem_sucesso", e.target.value)}
                  placeholder="Exibida após o envio (ex: Recebemos seus dados!)"
                />
              </FieldStack>

              <FieldStack className="full">
                <label className="input-label" htmlFor="form-redirect">
                  URL de redirecionamento (opcional)
                </label>
                <input
                  id="form-redirect"
                  className="input"
                  type="url"
                  value={settings.config.redirect_url}
                  maxLength={500}
                  onChange={(e) => setConfig("redirect_url", e.target.value)}
                  onBlur={(e) => completarRedirect(e.target.value)}
                  placeholder="https://www.seusite.com.br/obrigado"
                  aria-invalid={Boolean(erroRedirect)}
                  aria-describedby={erroRedirect ? "form-redirect-erro form-redirect-ajuda" : "form-redirect-ajuda"}
                />
                {erroRedirect && (
                  <AvisoDoCampo id="form-redirect-erro" $tom="danger" role="alert">
                    {erroRedirect}
                  </AvisoDoCampo>
                )}
                <span id="form-redirect-ajuda" className="input-help">
                  Vale só para a página hospedada deste formulário
                  {settings.slug ? ` (/f/${settings.slug})` : ""}: depois do envio,
                  o visitante vai para este endereço em vez de ver a mensagem de
                  sucesso. A página de obrigado do seu site fica na aba Publicar.
                </span>
              </FieldStack>

              {/* Campos de lead — so para CAPTURA/CAMPANHA */}
              {geraLead && (
                <>
                  <FieldStack>
                    <label className="input-label" htmlFor="form-etapa">
                      Etapa de destino (CRM)
                    </label>
                    <select
                      id="form-etapa"
                      className="select"
                      value={settings.etapa_destino ?? ""}
                      disabled={opcoesIndisponiveis}
                      onChange={(e) =>
                        setCampoSettings("etapa_destino", e.target.value)
                      }
                      aria-describedby={
                        etapaForaDoPipeline ? "form-etapa-aviso" : undefined
                      }
                    >
                      <option value="">— Etapa de entrada padrão —</option>
                      {/* A coluna gravada que nao esta nas opcoes continua
                          visivel: escondendo-a, o select mostrava "Etapa de
                          entrada padrão" e o gestor nao tinha como descobrir
                          por que a captacao parou (FORM-01). */}
                      {mostrarEtapaSalva && (
                        <option value={settings.etapa_destino}>
                          {rotuloEtapaSalva}
                        </option>
                      )}
                      {etapas.map((et) => (
                        <option key={et.id} value={et.id}>
                          {et.nome}
                        </option>
                      ))}
                    </select>
                    {etapaForaDoPipeline && (
                      <AvisoDoCampo id="form-etapa-aviso" $tom="warning" role="status">
                        Esta coluna saiu do pipeline: os leads deste formulário
                        estão entrando na coluna de entrada do CRM. Escolha uma
                        coluna ativa.
                      </AvisoDoCampo>
                    )}
                  </FieldStack>

                  <FieldStack>
                    <label className="input-label" htmlFor="form-origem">
                      Origem do lead
                    </label>
                    <select
                      id="form-origem"
                      className="select"
                      value={settings.origem_lead || ORIGEM_LEAD_PADRAO}
                      onChange={(e) =>
                        setCampoSettings("origem_lead", e.target.value)
                      }
                    >
                      {ORIGENS_LEAD.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <span className="input-help">
                      É a origem de todo lead deste formulário — a mesma que o
                      relatório de origem soma.
                    </span>
                  </FieldStack>

                  <FieldStack className="full">
                    <label className="input-label" htmlFor="form-resp">
                      Responsável padrão
                    </label>
                    <select
                      id="form-resp"
                      className="select"
                      value={settings.responsavel_padrao ?? ""}
                      disabled={opcoesIndisponiveis}
                      onChange={(e) =>
                        setCampoSettings("responsavel_padrao", e.target.value)
                      }
                    >
                      <option value="">— Sem responsável (atribuir depois no CRM) —</option>
                      {mostrarResponsavelSalvo && (
                        <option value={settings.responsavel_padrao}>
                          {rotuloResponsavelSalvo}
                        </option>
                      )}
                      {usuarios.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.nome || u.email}
                        </option>
                      ))}
                    </select>
                    <span className="input-help">
                      Leads capturados por este formulário serão atribuídos a este
                      vendedor. A lista traz só quem está ativo: um vendedor
                      desligado continuaria recebendo os leads do site sem
                      ninguém ver. Sem responsável: o lead aparece no sino e em
                      Agenda de quem pode atribuir leads.
                    </span>
                  </FieldStack>
                </>
              )}

              {/* Tags padrao — valem para quem cria E para quem enriquece */}
              {usaTags && (
                <FieldStack className="full">
                  <span className="input-label" id="form-tags-rotulo">
                    Tags padrão do lead
                  </span>
                  <TagsInput
                    value={settings.tags_padrao}
                    onChange={aplicarTags}
                    idPrefix="form-tags"
                    rotuloDoCampo="Nova tag padrão"
                    placeholder="Ex: feira-out-26 + Enter"
                    maxLength={MAX_TAMANHO_TAG}
                  />
                  {erroTags && (
                    <AvisoDoCampo $tom="danger" role="alert">
                      {erroTags}
                    </AvisoDoCampo>
                  )}
                  <span className="input-help">
                    Todo lead criado ou enriquecido por este formulário recebe
                    estas tags — é assim que a campanha fica marcada no CRM.
                    Vazio = nenhuma. Até {MAX_TAGS_PADRAO} tags de{" "}
                    {MAX_TAMANHO_TAG} caracteres.
                  </span>
                </FieldStack>
              )}
            </SettingsGrid>
          </EditorFieldset>
        </EditorCardBody>
      </EditorCard>

      {/* ---- (b) Canvas + (c) Preview ---- */}
      <EditorLayout>
        <EditorMain>
          <EditorCard>
            <EditorCardHead>
              <h2>Campos ({campos.length})</h2>
              {/* silentError: o erro da API ja sai no toast automatico do
                  request; sem isso eram dois toasts (FORM-41). */}
              {podeEditar && (
                <AsyncButton
                  variant="primary"
                  onClick={salvarTodosCampos}
                  successMessage="Campos salvos."
                  silentError
                >
                  <FiSave size={14} aria-hidden="true" /> Salvar campos
                </AsyncButton>
              )}
            </EditorCardHead>
            <EditorCardBody>
              {campos.length === 0 ? (
                <p className="muted" style={{ fontSize: 13, margin: "4px 0 16px" }}>
                  {podeEditar
                    ? "Nenhum campo ainda. Adicione abaixo para montar o formulario."
                    : "Nenhum campo ainda."}
                </p>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={campos.map((c) => String(c._key))}
                    strategy={verticalListSortingStrategy}
                  >
                    <CamposLista>
                      {campos.map((campo) => (
                        <CampoCard
                          key={campo._key}
                          campo={campo}
                          onEditar={() => abrirEdicao(campo)}
                          onExcluir={() => excluirCampo(campo)}
                          somenteLeitura={!podeEditar}
                        />
                      ))}
                    </CamposLista>
                  </SortableContext>
                </DndContext>
              )}

              {/* Palette de tipos */}
              {podeEditar && (
                <div
                  style={{
                    marginTop: 18,
                    paddingTop: 14,
                    borderTop: "1px solid var(--divider)",
                  }}
                >
                  <div
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 10,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "var(--ink-3)",
                      marginBottom: 10,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <FiPlus size={12} aria-hidden="true" /> Adicionar campo
                  </div>
                  <PaletteGrid>
                    {TIPOS_CAMPO.map((t) => (
                      <PaletteItem
                        key={t.value}
                        type="button"
                        onClick={() => adicionarCampo(t.value)}
                      >
                        <FiPlus aria-hidden="true" />
                        {t.label}
                      </PaletteItem>
                    ))}
                    <PaletteItem
                      type="button"
                      onClick={adicionarBlocoEndereco}
                      title="Adiciona CEP, rua, número, complemento, bairro, cidade e UF de uma vez"
                    >
                      <FiPlus aria-hidden="true" />
                      Endereço
                    </PaletteItem>
                  </PaletteGrid>
                </div>
              )}
            </EditorCardBody>
          </EditorCard>
        </EditorMain>

        <EditorAside>
          <EditorCard>
            <EditorCardHead>
              <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <FiEye size={14} aria-hidden="true" /> Pre-visualizacao
              </h2>
            </EditorCardHead>
            <EditorCardBody>
              <PreviewFrame>
                <PreviewHeader>
                  <h1 className="titulo">{tituloPublico || "Seu formulario"}</h1>
                  {(settings.config.subtitulo_publico || settings.descricao) && (
                    <div className="sub">
                      {settings.config.subtitulo_publico || settings.descricao}
                    </div>
                  )}
                </PreviewHeader>
                <FormRenderer
                  key={previewKey}
                  campos={campos}
                  config={settings.config}
                  themeColor={settings.config.tema_cor}
                  submitLabel={settings.config.design?.botao_texto || "Enviar"}
                  readOnly
                  buscarCep={buscarCepDoPreview}
                />
              </PreviewFrame>
            </EditorCardBody>
          </EditorCard>
        </EditorAside>
      </EditorLayout>

      {/* Painel de edicao de campo */}
      <CampoEditorPanel
        open={Boolean(editando)}
        campo={editando?.campo}
        outrosCampos={editando ? camposCitaveis(campos, editando.campo._key) : []}
        destinosUsados={destinosUsadosPorOutros(campos, editando?.campo?._key)}
        onClose={fecharPainel}
        onSave={salvarCampoEditado}
        somenteLeitura={!podeEditar}
      />

      <ConfirmDialog
        open={Boolean(excluindo)}
        titulo={excluindo ? `Excluir o campo “${rotuloDoCampo(excluindo)}”?` : ""}
        descricao={`${totalRespostas} resposta(s) guardam este campo; elas continuam visíveis em Respostas e na exportação, marcadas como campo removido. Novas respostas não terão mais este campo.`}
        confirmarLabel="Excluir campo"
        cancelarLabel="Manter campo"
        tone="danger"
        onConfirmar={() => {
          removerDoCanvas(excluindo);
          setExcluindo(null);
        }}
        onCancelar={() => setExcluindo(null)}
      />

      <ConfirmDialog
        open={Boolean(confirmacao)}
        titulo={confirmandoAbrir ? "Abrir a versão salva?" : "Sair sem salvar?"}
        descricao={
          confirmandoAbrir
            ? `A página pública mostra o que já está salvo: as alterações em ${blocosPorExtenso} ainda não aparecem nela.`
            : `Há alterações não salvas em ${blocosPorExtenso}. Se sair agora, elas se perdem.`
        }
        confirmarLabel={confirmandoAbrir ? "Abrir mesmo assim" : "Sair sem salvar"}
        cancelarLabel="Continuar editando"
        tone={confirmandoAbrir ? "primary" : "danger"}
        onConfirmar={() => {
          const { acao } = confirmacao;
          setConfirmacao(null);
          acao();
        }}
        onCancelar={cancelarConfirmacao}
      />
    </PageContainer>
  );
}
